import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useWebSocket } from '../hooks/useWebSocket';
import { useApi } from '../hooks/useApi';

const WebSocketContext = createContext(null);

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const { request } = useApi();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const organizationId = user?.organization?.id || user?.organizationId;
  const userId = user?.id;

  const listenersRef = useRef(new Set());

  const subscribe = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  // ── Fetch initial notifications ─────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const response = await request('get', '/api/notifications').catch(() => []);
      setNotifications(response || []);
      const unread = (response || []).filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId, request]);

  const handleMessage = useCallback((data) => {
    console.log('📨 WebSocket message received:', data);

    switch (data.type) {
      case 'NEW_NOTIFICATION':
        console.log('🔔 New notification via WebSocket:', data);
        if (data.notification) {
          setNotifications(prev => [data.notification, ...prev]);
          setUnreadCount(prev => prev + 1);
        }
        break;

      case 'NEW_MESSAGE':
      case 'AGENT_REQUIRED':
      case 'MODE_CHANGED':
      case 'ESCALATION_RESOLVED':
      case 'NEW_ESCALATION':
        break;

      default:
        console.log('Unknown message type:', data.type);
    }

    listenersRef.current.forEach((cb) => {
      try {
        cb(data);
      } catch (err) {
        console.error('WebSocket listener error:', err);
      }
    });
  }, []);

  const ws = useWebSocket(organizationId, userId, handleMessage);

  useEffect(() => {
    if (organizationId) {
      fetchNotifications();
    }
  }, [organizationId, fetchNotifications]);

  return (
    <WebSocketContext.Provider
      value={{
        ...ws,
        subscribe,
        notifications,
        setNotifications,
        unreadCount,
        setUnreadCount,
        loading,
        fetchNotifications,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;